import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Save, X, ShoppingBag } from "lucide-react";

const TYPE_LABELS = {
  avatar_frame: "Khung Avatar",
  rank_frame: "Khung Rank",
  title: "Danh hiệu",
  tv_effect: "Hiệu ứng TV",
};
const RARITIES = ["common", "rare", "epic", "legendary"];

const EMPTY = {
  name: "",
  description: "",
  type: "avatar_frame",
  rarity: "common",
  price_vnd: 0,
  price_points: 100,
  asset: "linear-gradient(135deg,#fb923c,#c2410c)",
  icon: "",
  active: true,
  sort: 0,
};

export default function AdminItems() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // item or "new"
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const { data } = await api.get("/admin/items");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (i) => { setEditing(i); setForm({ ...EMPTY, ...i }); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    try {
      const payload = { ...form, price_vnd: Number(form.price_vnd) || 0, price_points: Number(form.price_points) || 0, sort: Number(form.sort) || 0 };
      if (editing === "new") {
        await api.post("/admin/items", payload);
        toast.success("Đã tạo vật phẩm");
      } else {
        await api.patch(`/admin/items/${editing.id}`, payload);
        toast.success("Đã cập nhật");
      }
      cancel();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lưu thất bại");
    }
  };

  const del = async (i) => {
    if (!window.confirm(`Xóa "${i.name}"? Mọi người sở hữu nó sẽ mất.`)) return;
    try {
      await api.delete(`/admin/items/${i.id}`);
      toast.success("Đã xóa");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lỗi");
    }
  };

  const grouped = items.reduce((acc, i) => { (acc[i.type] = acc[i.type] || []).push(i); return acc; }, {});

  return (
    <div className="p-5 sm:p-8 space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShoppingBag className="text-amber-300" />
          <div>
            <h1 className="font-display font-black text-2xl">Vật phẩm cửa hàng</h1>
            <p className="text-xs text-white/50">Quản lý khung, danh hiệu, hiệu ứng TV</p>
          </div>
        </div>
        <button onClick={startNew} data-testid="admin-new-item" className="px-4 py-2 rounded-md bg-amber-400 text-black font-bold text-sm flex items-center gap-2 hover:bg-amber-300">
          <Plus size={14} /> Thêm vật phẩm
        </button>
      </div>

      {editing && (
        <div className="rounded-2xl bg-[#0c0d14] border border-amber-400/30 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold">{editing === "new" ? "Vật phẩm mới" : `Sửa: ${editing.name}`}</h2>
            <button onClick={cancel} className="text-white/40 hover:text-white"><X size={16} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Tên" v={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="item-name" />
            <Select label="Loại" v={form.type} onChange={(v) => setForm({ ...form, type: v })} options={Object.entries(TYPE_LABELS).map(([k, l]) => [k, l])} />
            <Select label="Độ hiếm" v={form.rarity} onChange={(v) => setForm({ ...form, rarity: v })} options={RARITIES.map((r) => [r, r])} />
            <Field label="Sort" v={form.sort} onChange={(v) => setForm({ ...form, sort: v })} type="number" />
            <Field label="Giá VND (0 = không bán bằng VND)" v={form.price_vnd} onChange={(v) => setForm({ ...form, price_vnd: v })} type="number" />
            <Field label="Giá Điểm (0 = không bán bằng điểm)" v={form.price_points} onChange={(v) => setForm({ ...form, price_points: v })} type="number" />
            <Field label="Icon emoji" v={form.icon || ""} onChange={(v) => setForm({ ...form, icon: v })} />
            <Field label="Asset (gradient/text/effect-code)" v={form.asset} onChange={(v) => setForm({ ...form, asset: v })} testid="item-asset" />
            <Field label="Mô tả" v={form.description || ""} onChange={(v) => setForm({ ...form, description: v })} cls="sm:col-span-2" />
            <label className="flex items-center gap-2 text-sm text-white/70 sm:col-span-2">
              <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Đang bật bán
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={cancel} className="px-4 py-2 rounded-md text-sm bg-white/5 border border-white/10">Hủy</button>
            <button onClick={save} data-testid="admin-save-item" className="px-4 py-2 rounded-md text-sm bg-amber-400 text-black font-bold flex items-center gap-2"><Save size={14} /> Lưu</button>
          </div>
        </div>
      )}

      {Object.keys(TYPE_LABELS).map((t) => (
        <div key={t} className="rounded-2xl bg-[#0c0d14] border border-white/8 p-4">
          <div className="font-display font-bold text-sm uppercase tracking-wider mb-3 text-amber-300">{TYPE_LABELS[t]}</div>
          {(grouped[t] || []).length === 0 ? (
            <div className="text-white/40 text-xs">Chưa có</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {(grouped[t] || []).map((i) => (
                <div key={i.id} className="rounded-lg bg-white/[0.02] border border-white/8 p-3 flex items-center gap-3">
                  <div className="text-2xl">{i.icon || "•"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">{i.name}</div>
                    <div className="text-[10px] text-white/50">{i.rarity} · {i.price_vnd ? `${i.price_vnd}đ` : "—"} / {i.price_points || 0} pts</div>
                    {!i.active && <div className="text-[10px] text-red-300">Đã tắt</div>}
                  </div>
                  <button onClick={() => startEdit(i)} className="p-2 rounded text-cyan-300 hover:bg-cyan-400/10" data-testid={`edit-item-${i.id}`}><Edit2 size={13} /></button>
                  <button onClick={() => del(i)} className="p-2 rounded text-red-400 hover:bg-red-400/10" data-testid={`del-item-${i.id}`}><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const Field = ({ label, v, onChange, type = "text", cls = "", testid }) => (
  <div className={cls}>
    <label className="text-[10px] uppercase tracking-wider text-white/50 block mb-1">{label}</label>
    <input data-testid={testid} type={type} value={v} onChange={(e) => onChange(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm outline-none focus:border-cyan-400/60" />
  </div>
);

const Select = ({ label, v, onChange, options }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-white/50 block mb-1">{label}</label>
    <select value={v} onChange={(e) => onChange(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm outline-none focus:border-cyan-400/60">
      {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
    </select>
  </div>
);
